import React, { useState } from "react";
import { Card, Button, Modal, Form, Badge } from "react-bootstrap";

const IngredientItem = ({ ingredient, updateIngredient, removeIngredient, addToShoppingList, addNotification }) => {
    const [show, setShow] = useState(false);
    const [amount, setAmount] = useState(parseFloat(String(ingredient.amount).replace(",", ".")) || 0);
    const [unit, setUnit] = useState(ingredient.unit);
    const [expiration, setExpiration] = useState(ingredient.expiration);
    const [isBasic, setIsBasic] = useState(ingredient.isBasic || false);
    const [note, setNote] = useState(ingredient.note || "");
    const [minAmount, setMinAmount] = useState(parseFloat(String(ingredient.minAmount).replace(",", ".")) || 1);

    // Výpočet zbývajících dnů do expirace
    const expDate = new Date(expiration);
    const today = new Date();
    const differenceInDays = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));

    // Formátování data do dd.mm.yyyy
    const formattedExpiration = expDate.toLocaleDateString("cs-CZ", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });

    // Určení barvy podle expirace a množství pod limitem
    let borderColor = "";
    let textColor = "text-warning";
    let warningText = "";

    if (differenceInDays <= 3) {
        borderColor = "border-danger";
        textColor = "text-danger fw-bold";
    } else if (differenceInDays <= 7) {
        borderColor = "border-warning";
        textColor = "text-warning fw-bold";
    }

    if (amount < minAmount) {
        borderColor = "border-danger";
        warningText = "⚠️ Množství pod minimem!";
    }

    const handleSave = () => {
        updateIngredient(ingredient.id, {
            amount,
            unit,
            expiration,
            isBasic,
            note,
            minAmount
        });
        setShow(false);
    };

    const handleRemove = () => {
        if (isBasic) {
            updateIngredient(ingredient.id, { amount: 0 });
        } else {
            removeIngredient(ingredient.id);
        }
    };

    const handleAddToShoppingList = () => {
        if (addToShoppingList) {
            const newItem = {
                id: ingredient.id,
                name: ingredient.name,
                amount: minAmount - amount,
                unit: ingredient.unit
            };

            addToShoppingList(newItem);

            // Přidání oznámení
            if (addNotification) {
                addNotification(`🛒 Položka ${newItem.name} byla přidána na nákupní seznam!`);
            }
        } else {
            console.error("⚠️ Chyba: addToShoppingList není definováno!");
        }
    };

    return (
        <>
            <Card className={`mb-3 shadow-sm ${borderColor} position-relative`}>
                <Card.Body>
                    {isBasic && (
                        <Badge bg="success" className="position-absolute top-0 end-0 m-2">
                            ⭐ Základní
                        </Badge>
                    )}
                    <Card.Title className="text-primary">{ingredient.name}</Card.Title>
                    <Card.Text>
                        Množství: <strong>{amount} {unit}</strong> <br />
                        Expirace: {" "}
                        <span className={textColor}>
                            {formattedExpiration} {differenceInDays <= 3 ?
                            (differenceInDays > 0 ?
                                (differenceInDays === 1 ? `⚠️ Expiruje za ${differenceInDays} den` : `⚠️ Expiruje za ${differenceInDays} dny`)
                                : `⚠️ Expirováno!`)
                            : ""}
                        </span><br/>
                        {warningText && <span className="text-danger fw-bold">{warningText}</span>}<br/>
                        {(note ? `Poznámka: ${note}` : null)}
                    </Card.Text>
                    <Button variant="info" size="sm" onClick={() => setShow(true)}>
                        <i className="bi bi-pencil-fill"></i>️ Upravit
                    </Button>
                    <Button variant="danger" size="sm" className="ms-2" onClick={handleRemove}>
                        <i className="bi bi-trash3-fill"></i> Smazat
                    </Button>

                    {/* 🔥 Přidáno tlačítko "Přidat na nákupní seznam" 🔥 */}
                    {amount < minAmount && (
                        <Button variant="warning" size="sm" className="ms-2" onClick={() => handleAddToShoppingList()}>
                            <i className="bi bi-cart-plus"></i> Přidat na nákupní seznam
                        </Button>
                    )}
                </Card.Body>
            </Card>

            {/* EDITAČNÍ MODÁLNÍ OKNO */}
            <Modal show={show} onHide={() => setShow(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Upravit ingredienci</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Množství</Form.Label>
                            <Form.Control
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(parseFloat(e.target.value.replace(",", ".")) || 0)}
                                min="0" step="0.1"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Jednotka</Form.Label>
                            <Form.Select value={unit} onChange={(e) => setUnit(e.target.value)}>
                                <option value="ks">Kusy</option>
                                <option value="l">Litry</option>
                                <option value="g">Gramy</option>
                                <option value="ml">Mililitry</option>
                                <option value="kg">Kilogramy</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Datum expirace</Form.Label>
                            <Form.Control
                                type="date"
                                value={expiration}
                                onChange={(e) => setExpiration(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Minimální množství</Form.Label>
                            <Form.Control
                                type="number"
                                value={minAmount}
                                onChange={(e) => setMinAmount(parseFloat(e.target.value.replace(",", ".")) || 1)}
                                min="0.1" step="0.1"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="📌 Označit jako základní ingredienci"
                                checked={isBasic}
                                onChange={(e) => setIsBasic(e.target.checked)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Poznámka 📝</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Např. Koupit v akci, Použít na dort..."
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShow(false)}>Zavřít</Button>
                    <Button variant="success" onClick={handleSave}>💾 Uložit</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default IngredientItem;
